import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { ThemeToggle } from '../components/ThemeToggle';
import { LegalFooter } from '../components/LegalFooter';
import TestimonialsSection from '../components/TestimonialsSection';
import { ArrowRight, Zap, Target, Compass, Users, CheckCircle, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

function LandingPage() {
  const { user } = useAuth();

  const handleGetStarted = () => {
    if (user) {
      window.location.href = user.role === 'admin' ? '/admin' : '/dashboard';
    } else {
      window.location.href = '/login';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-50 h-12 overflow-hidden">
        <div className="container-custom h-full">
          <div className="flex items-center justify-between h-full">
            <a href="/" className="flex items-center gap-2" data-testid="logo">
              <Logo variant="symbol" size="default" />
              <span className="font-semibold text-foreground text-sm tracking-tight hidden sm:inline">secret<span className="text-muted-foreground">sauce</span></span>
            </a>
            <nav className="flex items-center gap-4">
              <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-smooth hidden sm:block">
                How It Works
              </a>
              <a href="#testimonials" className="text-sm text-muted-foreground hover:text-foreground transition-smooth hidden sm:block">
                Testimonials
              </a>
              <ThemeToggle />
              {user ? (
                <Button onClick={handleGetStarted} data-testid="nav-dashboard-btn">
                  Go to Dashboard
                </Button>
              ) : (
                <Button onClick={handleGetStarted} data-testid="nav-login-btn">
                  Login
                </Button>
              )}
            </nav>
          </div>
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 pointer-events-none" />
        <div className="container-custom relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              THE PATH PROGRAMME
            </div>
            <h1 className="h1 mb-6">
              The Operating System for{' '}
              <span className="bg-gradient-to-r from-accent to-ss-purple-light bg-clip-text text-transparent">Entrepreneurial Leadership</span>
            </h1>
            <p className="body-large text-muted-foreground mb-8 max-w-2xl mx-auto">
              Discover your edge. Understand your core. Chart your path. 
              A premium diagnostic and development programme for senior leaders 
              navigating complexity and driving disproportionate impact.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleGetStarted} 
                size="lg"
                className="gap-2"
                data-testid="cta-get-started"
              >
                {user ? 'Go to Dashboard' : 'Start Your EDGE Diagnostic'}
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                asChild
                data-testid="cta-learn-more"
              >
                <a href="#how-it-works">Learn More</a>
              </Button>
            </div>
            <p className="text-base text-muted-foreground mt-4">
              <span className="font-semibold text-foreground">Free EDGE Diagnostic</span> • 10-15 minutes • Instant insights
            </p>
          </div>
        </div>
      </section>

      {/* The Shift Section */}
      <section className="py-16 bg-muted/30 border-y border-border">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <p className="eyebrow mb-4">THE SHIFT</p>
            <h2 className="h3 mb-6">Value creation has fundamentally changed</h2>
            <p className="body text-muted-foreground">
              In a fast-paced AI world, technical skills quickly become common. 
              The leaders who thrive aren&apos;t the ones who outwork everyone—they&apos;re the ones 
              who understand their unique pressures, leverage their authentic operating style, 
              and create value through influence rather than effort alone.
            </p>
          </div>
        </div>
      </section>

      {/* What is PATH */}
      <section className="py-20">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <p className="eyebrow mb-4">WHAT IS PATH</p>
              <h2 className="h2 mb-4">Your leadership operating system</h2>
              <p className="body-large text-muted-foreground max-w-2xl mx-auto">
                PATH is a framework for understanding and optimising how you lead. 
                It starts with diagnostics and evolves into a complete development programme.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-card border border-border rounded-xl p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-accent">P</span>
                </div>
                <h3 className="font-heading font-semibold mb-2">Purpose</h3>
                <p className="text-sm text-muted-foreground">
                  Clarify your driving motivations and authentic leadership identity
                </p>
              </div>
              <div className="bg-card border border-border rounded-xl p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-accent">A</span>
                </div>
                <h3 className="font-heading font-semibold mb-2">Action</h3>
                <p className="text-sm text-muted-foreground">
                  Identify high-leverage behaviours aligned with your natural style
                </p>
              </div>
              <div className="bg-card border border-border rounded-xl p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-accent">T</span>
                </div>
                <h3 className="font-heading font-semibold mb-2">Transformation</h3>
                <p className="text-sm text-muted-foreground">
                  Bridge the gap between your stated self and your core patterns
                </p>
              </div>
              <div className="bg-card border border-border rounded-xl p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-accent">H</span>
                </div>
                <h3 className="font-heading font-semibold mb-2">Habit</h3>
                <p className="text-sm text-muted-foreground">
                  Build sustainable systems for continuous leadership growth
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Diagnostics First */}
      <section className="py-20 bg-[#24064C]">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <p className="uppercase tracking-[0.15em] text-sm font-medium mb-4 text-white/80">WHY DIAGNOSTICS FIRST</p>
              <h2 className="text-3xl sm:text-4xl font-heading font-semibold leading-snug mb-4 text-white">You can't optimise what you don&apos;t understand</h2>
              <p className="text-lg leading-relaxed max-w-2xl mx-auto text-white font-normal">
                Before any development programme can work, you need an honest, nuanced 
                picture of where you are. Our AI-powered diagnostics surface insights 
                that self-reflection alone cannot reach.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EDGE & CORE Diagnostics */}
      <section id="how-it-works" className="py-20">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <p className="eyebrow mb-4">THE DIAGNOSTICS</p>
              <h2 className="h2 mb-4">Dual perspectives for complete clarity</h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* EDGE Diagnostic */}
              <div className="bg-card border-2 border-ss-orange/30 rounded-xl p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-ss-orange/10 rounded-bl-full" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 bg-ss-orange/10 text-ss-orange px-3 py-1 rounded-full text-sm font-medium mb-4">
                    <Target className="w-4 h-4" />
                    EDGE DIAGNOSTIC
                  </div>
                  <h3 className="h4 mb-3">Understand Your Pressures</h3>
                  <p className="body text-muted-foreground mb-6">
                    What creates tension for you? Where do you feel the squeeze? 
                    EDGE surfaces the specific pressures that shape your leadership responses—some 
                    lead to growth, others to anxiety, others to breakthrough energy.
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-ss-teal flex-shrink-0" />
                      <span>10-15 minute AI-adaptive conversation</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-ss-teal flex-shrink-0" />
                      <span>Personalised Edge Profile</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-ss-teal flex-shrink-0" />
                      <span>Complimentary—start immediately</span>
                    </li>
                  </ul>
                  <Button 
                    onClick={handleGetStarted}
                    className="w-full bg-ss-orange hover:bg-ss-orange/90 text-white"
                    data-testid="cta-edge"
                  >
                    Start EDGE Diagnostic
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>

              {/* CORE Diagnostic */}
              <div className="bg-card border-2 border-ss-teal/30 rounded-xl p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-ss-teal/10 rounded-bl-full" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 bg-ss-teal/10 text-ss-teal px-3 py-1 rounded-full text-sm font-medium mb-4">
                    <Compass className="w-4 h-4" />
                    CORE DIAGNOSTIC
                  </div>
                  <h3 className="h4 mb-3">Reveal Your Operating Style</h3>
                  <p className="body text-muted-foreground mb-6">
                    Who do you believe yourself to be? Who does your behaviour reveal? 
                    CORE uncovers your leadership archetype—both your Stated self and your 
                    Core patterns—and the gap between them.
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-ss-teal flex-shrink-0" />
                      <span>15-30 minute depth diagnostic</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-ss-teal flex-shrink-0" />
                      <span>Stated & Core Archetype analysis</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-ss-teal flex-shrink-0" />
                      <span>By invitation after EDGE completion</span>
                    </li>
                  </ul>
                  <Button 
                    variant="outline"
                    className="w-full border-ss-teal/50 text-foreground hover:bg-ss-teal/10"
                    disabled
                  >
                    Available After EDGE
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Journey */}
      <section className="py-20 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <p className="eyebrow mb-4">YOUR JOURNEY</p>
              <h2 className="h2 mb-4">Three steps to transformation</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4 relative">
                  <span className="text-2xl font-bold text-accent">1</span>
                </div>
                <h3 className="font-heading font-semibold mb-2">Complete EDGE</h3>
                <p className="text-sm text-muted-foreground">
                  Take the complimentary EDGE diagnostic to understand your unique pressure points
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-accent">2</span>
                </div>
                <h3 className="font-heading font-semibold mb-2">Unlock CORE</h3>
                <p className="text-sm text-muted-foreground">
                  Receive your invitation to CORE and discover your leadership archetype
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-accent">3</span>
                </div>
                <h3 className="font-heading font-semibold mb-2">Apply for PATH</h3>
                <p className="text-sm text-muted-foreground">
                  Join our exclusive, high-calibre cohort for intensive leadership development
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PATH Cohort */}
      <section className="py-20">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#24064C] to-[#5B21B6] rounded-2xl p-8 lg:p-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-white/20 text-white px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <Users className="w-4 h-4" />
                  PATH
                </div>
                <h2 className="text-3xl sm:text-4xl font-heading font-semibold leading-snug mb-4 text-white">For leaders ready to go deeper</h2>
                <p className="text-lg leading-relaxed mb-6 max-w-2xl text-white font-normal">
                  Exclusively for senior leaders seeking to amplify their impact, The PATH Programme offers an intensive development experience. With curated enrolment of a select group of high-calibre participants, it promises transformational outcomes.
                </p>
                <ul className="grid sm:grid-cols-2 gap-3 mb-8">
                  <li className="flex items-center gap-2 text-sm text-white">
                    <CheckCircle className="w-4 h-4 text-white flex-shrink-0" />
                    <span>Small cohort, high engagement</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-white">
                    <CheckCircle className="w-4 h-4 text-white flex-shrink-0" />
                    <span>Facilitated 1:1 sessions</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-white">
                    <CheckCircle className="w-4 h-4 text-white flex-shrink-0" />
                    <span>Peer learning network</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-white">
                    <CheckCircle className="w-4 h-4 text-white flex-shrink-0" />
                    <span>12-month development arc</span>
                  </li>
                </ul>
                <p className="text-sm text-white/80">
                  Applications open after completing both EDGE and CORE diagnostics
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="eyebrow mb-4">WHO THIS IS FOR</p>
              <h2 className="h2 mb-4">Senior leaders who want more</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium mb-1">Impact Builders</p>
                  <p className="text-sm text-muted-foreground">
                    Creating meaningful change that outlasts your tenure
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium mb-1">Leverage Creators</p>
                  <p className="text-sm text-muted-foreground">
                    Building influence and results without waiting for permission
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium mb-1">Portfolio Thinkers</p>
                  <p className="text-sm text-muted-foreground">
                    Managing multiple priorities while maintaining strategic focus
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium mb-1">Fulfillment Seekers</p>
                  <p className="text-sm text-muted-foreground">
                    Aligning professional success with personal satisfaction
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <div id="testimonials">
        <TestimonialsSection />
      </div>

      {/* Final CTA Section */}
      <section className="py-20 bg-slate-50 dark:bg-slate-900">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold leading-snug mb-4 text-slate-800 dark:text-white">Ready to discover your edge?</h2>
          <p className="text-lg leading-relaxed mb-8 max-w-2xl mx-auto text-slate-600 dark:text-slate-300">
            Start with the complimentary EDGE Diagnostic. 10-15 minutes to your first insights.
          </p>
          <Button 
            onClick={handleGetStarted}
            size="lg"
            className="gap-2"
            data-testid="cta-final"
          >
            {user ? 'Go to Dashboard' : 'Start EDGE Diagnostic'}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* About Secret Sauce */}
      <section className="py-16 border-t border-border">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <Logo variant="symbol" size="large" className="mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-lg mb-2 text-[#24064C] dark:text-white">About Secret Sauce</h3>
            <p className="text-base text-slate-600 dark:text-slate-300">
              Secret Sauce builds premium tools and programmes for high-performing leaders 
              who want disproportionate impact. The PATH Programme is our flagship operating system for 
              entrepreneurial leadership.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <LegalFooter />
    </div>
  );
}

export default LandingPage;
